import { memo, useMemo } from 'react'
import styled from 'styled-components'

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.33" strokeLinecap="square" strokeLinejoin="round" />
  </svg>
)

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 12px;
  gap: 8px;
`

export const PaginationButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background-color: ${({ theme, disabled }) => (disabled ? theme.backgroundSurface : theme.backgroundOutline)};
  color: ${({ theme, disabled }) => (disabled ? theme.textTertiary : theme.textPrimary)};
  border: 1px solid ${({ theme, disabled }) => (disabled ? theme.borderSecondary : theme.borderSecondary)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  &:hover {
    background-color: ${({ theme, disabled }) => (disabled ? theme.backgroundSurface : theme.backgroundOutline)};
  }
`

export const PageNumber = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background-color: ${({ theme, active }) => (active ? theme.cardPrimary : theme.backgroundSurface)};
  color: ${({ theme, active }) => (active ? theme.white : theme.textPrimary)};
  border: 1px solid ${({ theme, active }) => (active ? theme.cardPrimary : theme.cardBorderPrimary)};
  cursor: ${({ active }) => (active ? 'default' : 'pointer')};

  &:hover {
    background-color: ${({ theme, active }) => (active ? theme.cardPrimary : theme.backgroundOutline)};
  }
`

export const EllipsisSpan = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: ${({ theme }) => theme.textSecondary};
`

export const SearchResultStats = styled.div`
  display: flex;
  justify-content: center;
  margin: 8px 0;
  font-size: 12px;
  color: ${({ theme }) => theme.textTertiary};
`

export const CompactPaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin: 20px 0;
`

export const CompactPageNumbersWrapper = styled.div`
  display: flex;
  gap: 4px;
`

export const CompactPaginationButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 2px;
  background-color: ${({ disabled }) => (disabled ? 'transparent' : 'var(--color-portfolio-filter-button-bg)')};
  color: ${({ disabled }) =>
    disabled ? 'var(--color-portfolio-category-border)' : 'var(--color-kodiak-text-primary)'};
  border: 1px solid ${({ disabled }) => (disabled ? 'transparent' : 'var(--color-portfolio-input-border)')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 640px) {
    padding: 8px;

    .button-text {
      display: none;
    }
  }
`

export const CompactPageNumber = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 8px 12px;
  border-radius: 2px;
  background-color: ${({ active }) => (active ? 'var(--color-kodiak-slider)' : 'transparent')};
  color: ${({ active }) => (active ? 'var(--color-kodiak-util-white)' : 'var(--color-kodiak-text-primary)')};
  border: 1px solid;
  border-color: ${({ active }) => (active ? 'var(--color-portfolio-second-border)' : 'transparent')};
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
`

export const CompactEllipsisSpan = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: var(--color-kodiak-text-primary);
  font-size: 12px;
`

// Reusable Pagination component
const Pagination = memo(
  ({
    currentPage,
    totalPages,
    onPageChange,
    variant = 'default',
  }: {
    currentPage: number
    totalPages: number
    onPageChange: (page: number) => void
    variant?: 'default' | 'compact'
  }) => {
    const Container = variant === 'compact' ? CompactPaginationContainer : PaginationContainer
    const Button = variant === 'compact' ? CompactPaginationButton : PaginationButton
    const PageNum = variant === 'compact' ? CompactPageNumber : PageNumber
    const Ellipsis = variant === 'compact' ? CompactEllipsisSpan : EllipsisSpan

    // Generate page numbers to display
    const pageNumbers = useMemo(() => {
      const pages = []
      const maxVisiblePages = 5

      if (totalPages <= maxVisiblePages) {
        // Show all pages if we have few pages
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        // Always show first page
        pages.push(1)

        // Calculate range around current page
        let rangeStart = Math.max(2, currentPage - 1)
        let rangeEnd = Math.min(totalPages - 1, currentPage + 1)

        // Adjust range for edge cases
        if (currentPage <= 2) {
          rangeEnd = Math.min(4, totalPages - 1)
        } else if (currentPage >= totalPages - 2) {
          rangeStart = Math.max(2, totalPages - 3)
        }

        // Add ellipsis before range if needed
        if (rangeStart > 2) {
          pages.push(-1) // -1 represents ellipsis
        }

        // Add range pages
        for (let i = rangeStart; i <= rangeEnd; i++) {
          pages.push(i)
        }

        // Add ellipsis after range if needed
        if (rangeEnd < totalPages - 1) {
          pages.push(-2) // -2 represents ellipsis
        }

        // Always show last page
        pages.push(totalPages)
      }

      return pages
    }, [currentPage, totalPages])

    if (totalPages <= 1) return null

    const pageNumbersContent = pageNumbers.map((pageNum, index) =>
      pageNum < 0 ? (
        <Ellipsis key={`ellipsis-${index}`}>…</Ellipsis>
      ) : (
        <PageNum key={pageNum} active={pageNum === currentPage} onClick={() => onPageChange(pageNum)}>
          {pageNum}
        </PageNum>
      )
    )

    return (
      <Container>
        <Button onClick={() => onPageChange(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>
          {variant === 'compact' ? (
            <>
              <span style={{ transform: 'rotate(180deg)', display: 'flex' }}>
                <ChevronRight />
              </span>
              <span className="button-text">Previous</span>
            </>
          ) : (
            '←'
          )}
        </Button>

        {variant === 'compact' ? (
          <CompactPageNumbersWrapper>{pageNumbersContent}</CompactPageNumbersWrapper>
        ) : (
          pageNumbersContent
        )}

        <Button
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          {variant === 'compact' ? (
            <>
              <span className="button-text">Next</span>
              <ChevronRight />
            </>
          ) : (
            '→'
          )}
        </Button>
      </Container>
    )
  }
)
Pagination.displayName = 'Pagination'

export default Pagination
